import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  formatTime,
  getTimeColorClass,
  formatStatus,
  formatDateToShortDotV2,
  BaseButton,
} from '@repo/common';
import OptionList from './OptionList';
import { PAYMENT_COLUMN_CLASS as COL, ADMIN_CARD_CLASS } from '../paymentColumns';

import { ReactComponent as OptionIcon } from '@repo/common/assets/icons/20px/icon_20px_option_filled.svg';
import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/20px/icon_20px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/20px/icon_20px_arrow-top.svg';
import { ReactComponent as DownloadIcon } from '@repo/common/assets/icons/20px/icon_20px_download.svg';

// 카드 결제는 기존 표기("15.00 $"), KRW 계좌이체는 "55,000 KRW"
const formatMoney = (value, currency) =>
  String(currency || '').toUpperCase() === 'KRW'
    ? `${Math.round(Number(value || 0)).toLocaleString('en-US')} KRW`
    : `${Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} $`;

const PaymentsRow = ({ item }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [showOptionList, setShowOptionList] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedOriginalStatus, setSelectedOriginalStatus] = useState(null);

  const workshopTitleText = Array.isArray(item.workshopTitles)
    ? item.workshopTitles.map((title) => `[Workshop] ${title}`).join(', ')
    : `[Workshop] ${item.workshopTitles}`;

  const totalAmount = Array.isArray(item.amounts)
    ? item.amounts.reduce((sum, v) => sum + Number(v || 0), 0)
    : Number(item.amount || 0);

  const formattedAmount = formatMoney(totalAmount, item.currency);
  const isBankTransfer =
    String(item.paymentProvider || '').toUpperCase() === 'BANK_TRANSFER';

  const getPaymentStatus = (statuses = []) => {
    if (!statuses.length) return 'UNPAID';

    const paidSet = ['CONFIRMED', 'ATTENDED', 'ABSENT'];

    const allCanceled = statuses.every((s) => s === 'CANCELED');
    if (allCanceled) return 'CANCELED';

    const paidCount = statuses.filter((s) => paidSet.includes(s)).length;

    if (paidCount === statuses.length) return 'FULLY PAID';
    if (paidCount > 0) return 'PARTIALLY PAID';

    return 'UNPAID';
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'FULLY PAID':
        return 'bg-green-light02 text-green-deep04';

      case 'PARTIALLY PAID':
        return 'bg-purple-purple01 text-purple-purple05';

      case 'CANCELED':
        return 'bg-grayscale-gray02 text-grayscale-gray05';

      case 'UNPAID':
        return 'bg-functional-failed01 text-functional-failed03';

      default:
        return 'bg-green-light02 text-green-deep04';
    }
  };

  const paymentStatus = getPaymentStatus(item.statuses);

  const handleContact = () => {
    if (!item.userEmail) return;
    window.open(`mailto:${item.userEmail}`, '_blank');
  };

  const handleDownloadReceipt = () => {
    // 계좌이체 결제는 PG 영수증이 없다 — null 로 새 탭을 열지 않는다
    if (item.receiptUrl) window.open(item.receiptUrl, '_blank');
    setShowOptionList(false);
  };

  const handleViewInvoice = () => {
    navigate(`/mypage?tab=Payments&invoice=${item.invoiceNumber}`);
    setShowOptionList(false);
  };

  // 항목별 HOLD/OFFERED 남은 시간
  const getSecondsLeft = (index) => {
    const status = item.statuses?.[index];
    let expiresAt = null;
    if (status === 'HOLD') expiresAt = item.holdExpiresAtList?.[index];
    else if (status === 'OFFERED') expiresAt = item.offerExpiresAtList?.[index];
    if (!expiresAt) return 0;
    const diff = Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000);
    return diff > 0 ? diff : 0;
  };

  const openOptions = (e, status, originalStatus) => {
    e.stopPropagation();
    setAnchorRect(e.currentTarget.getBoundingClientRect());
    setSelectedStatus(status);
    setSelectedOriginalStatus(originalStatus);
    setShowOptionList(true);
  };

  const invoiceOptionButton = (
    <button
      type='button'
      aria-label='Payment options'
      className='text-icon-placeholder hover:text-icon-tertiary active:text-icon-tertiary'
      onClick={(e) => openOptions(e, paymentStatus, item.statuses)}
    >
      <OptionIcon />
    </button>
  );

  const statusBadge = (
    <BaseButton
      hideIcon
      isStatic
      type='text'
      size='s'
      bgclass={`px-2 py-0.5 ${getStatusStyle(paymentStatus)} typo-b12Strong h-6`}
    >
      {paymentStatus}
    </BaseButton>
  );

  const renderItemStatus = (index) => {
    const status = item.statuses?.[index];
    const secondsLeft = getSecondsLeft(index);
    return (
      <>
        {formatStatus(status)}
        {item.canceledSerialNumbers?.[index] && <div>#{item.canceledSerialNumbers[index]}</div>}
        {(status === 'HOLD' || status === 'OFFERED') && (
          <span className={getTimeColorClass(secondsLeft)}>{formatTime(secondsLeft)}</span>
        )}
      </>
    );
  };

  const itemOptionButton = (index) =>
    formatStatus(item.statuses?.[index]) === 'Confirmed' && (
      <button
        type='button'
        aria-label='Item options'
        className='text-icon-placeholder hover:text-icon-tertiary active:text-icon-tertiary'
        onClick={(e) => openOptions(e, formatStatus(item.statuses?.[index]), selectedOriginalStatus)}
      >
        <OptionIcon />
      </button>
    );

  const downloadButton = item.receiptUrl ? (
    <button
      type='button'
      aria-label='Download receipt'
      onClick={handleDownloadReceipt}
      className='w-[30px] h-[30px] flex justify-center items-center text-icon-placeholder hover:text-icon-tertiary active:text-icon-tertiary'
    >
      <DownloadIcon />
    </button>
  ) : (
    <span
      className='w-[30px] h-[30px] flex justify-center items-center typo-b11 text-text-placeholder'
      title={isBankTransfer ? 'Bank transfer · no PG receipt' : 'No receipt'}
    >
      {isBankTransfer ? '₩' : '-'}
    </span>
  );

  return (
    <div className='w-full'>
      {/* 표 — tabletV(1180px) 이상 */}
      <div
        className={`hidden tabletV:flex flex-col w-full items-start ${
          isOpen ? 'py-1 gap-2 bg-surface-lightgray60' : ''
        }`}
      >
        <div className={`flex w-full h-[48px] items-center ${!isOpen ? 'bg-grayscale-gray00' : ''}`}>
          <div className={`flex justify-center items-center p-1 ${COL.date}`}>
            <span className='typo-b14 text-text-secondary block w-full text-center truncate'>
              {formatDateToShortDotV2(item.createdAt)}
            </span>
          </div>
          <div
            className={`flex justify-center items-center p-1 hover:underline cursor-pointer ${COL.invoice}`}
            onClick={handleViewInvoice}
          >
            <span className='typo-b14 text-text-secondary truncate'>{item.invoiceNumber}</span>
          </div>
          <div
            className={`flex justify-center items-center p-1 cursor-pointer hover:underline ${COL.item}`}
            onClick={handleViewInvoice}
          >
            <span className='typo-b14Strong text-text-primary truncate block w-full'>
              {workshopTitleText}
            </span>
          </div>
          <div className={`flex justify-center items-center p-1 ${COL.user}`}>
            <span className='typo-b14 text-text-secondary truncate block w-full text-center'>
              {item.userName}
            </span>
          </div>
          <div className={`flex justify-center items-center p-1 ${COL.amount}`}>
            <span className='typo-b14 text-text-secondary whitespace-nowrap'>{formattedAmount}</span>
          </div>
          <div className={`flex flex-col justify-center items-center p-1 ${COL.status}`}>
            {statusBadge}
          </div>
          <div className={`flex justify-center items-center p-1 ${COL.contact}`}>
            <BaseButton type='text_basic' size='s' onClick={handleContact} width='67px' hideIcon>
              Contact
            </BaseButton>
          </div>
          <div className={`flex justify-center items-center ${COL.icon}`}>{invoiceOptionButton}</div>
          <div className={`flex justify-start items-center ${COL.icon}`}>
            <button
              type='button'
              aria-label={isOpen ? 'Collapse' : 'Expand'}
              className='text-icon-placeholder'
              onClick={() => setIsOpen((prev) => !prev)}
            >
              {isOpen ? <ArrowTopIcon /> : <ArrowBottomIcon />}
            </button>
          </div>
        </div>

        {isOpen && (
          <div className='w-full flex flex-col gap-2'>
            {item.bookingIds.map((id, index) => (
              <div key={id} className='flex h-12 items-center typo-b14 text-text-secondary'>
                <span className={`flex p-1 ${COL.date}`} />
                <span className={`flex items-center justify-center p-1 gap-1 ${COL.invoice}`}>
                  <span className='text-text-placeholder'>Item ID</span>
                  <span>#{String(index + 1).padStart(2, '0')}</span>
                </span>
                <div className={`flex items-center justify-center p-1 gap-2.5 ${COL.item}`}>
                  <span
                    className='truncate block w-full typo-b14Strong text-text-primary hover:underline cursor-pointer'
                    onClick={() =>
                      navigate(`/community/kpathport/workshop/${item.workshopIds?.[index]}`)
                    }
                  >
                    [Workshop] {item.workshopTitles?.[index]}
                  </span>
                </div>
                <span className={`flex items-center justify-center p-1 gap-2.5 ${COL.user}`}>
                  <span className='truncate block w-full text-center'>{item.userName}</span>
                </span>
                <span className={`flex items-center justify-center p-1 gap-2.5 truncate ${COL.amount}`}>
                  {formatMoney(item.amounts?.[index], item.currency)}
                </span>
                <span className={`flex flex-col items-center justify-center p-1 ${COL.status}`}>
                  {renderItemStatus(index)}
                </span>
                <span className={`flex items-center justify-center p-1 gap-2.5 ${COL.contact}`}>
                  {downloadButton}
                </span>
                <div className={`flex justify-center items-center ${COL.icon}`}>
                  {itemOptionButton(index)}
                </div>
                <span className={`flex ${COL.icon}`} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 카드 — tabletV 미만 */}
      <div className={ADMIN_CARD_CLASS}>
        <div className='flex items-start justify-between gap-2'>
          <div className='flex flex-col min-w-0 gap-0.5 cursor-pointer' onClick={handleViewInvoice}>
            <span className='typo-b14Strong text-text-primary line-clamp-2'>{workshopTitleText}</span>
            <span className='typo-b12 text-text-secondary truncate underline'>
              {item.invoiceNumber} · {item.userName}
            </span>
          </div>
          <div className='flex-shrink-0 -mr-1'>{invoiceOptionButton}</div>
        </div>
        <div className='flex flex-wrap items-center gap-x-3 gap-y-1 typo-b13 text-text-secondary'>
          <span>{formatDateToShortDotV2(item.createdAt)}</span>
          <span className='typo-b13Strong text-text-primary'>{formattedAmount}</span>
          {statusBadge}
        </div>
        <div className='flex items-center justify-between'>
          <BaseButton type='text_basic' size='s' onClick={handleContact} hideIcon>
            Contact
          </BaseButton>
          <button
            type='button'
            className='flex items-center gap-1 typo-b13 text-text-secondary'
            onClick={() => setIsOpen((prev) => !prev)}
          >
            {item.bookingIds.length} {item.bookingIds.length === 1 ? 'item' : 'items'}
            {isOpen ? <ArrowTopIcon /> : <ArrowBottomIcon />}
          </button>
        </div>
        {isOpen && (
          <div className='flex flex-col gap-2 pt-2 border-t border-grayscale-gray02'>
            {item.bookingIds.map((id, index) => (
              <div key={id} className='flex items-center gap-2 typo-b13 text-text-secondary'>
                <span className='flex-shrink-0 text-text-placeholder'>
                  #{String(index + 1).padStart(2, '0')}
                </span>
                <div className='flex flex-col flex-1 min-w-0'>
                  <span
                    className='truncate typo-b13Strong text-text-primary'
                    onClick={() =>
                      navigate(`/community/kpathport/workshop/${item.workshopIds?.[index]}`)
                    }
                  >
                    [Workshop] {item.workshopTitles?.[index]}
                  </span>
                  <span className='flex flex-wrap gap-x-2 typo-b12'>{renderItemStatus(index)}</span>
                </div>
                <span className='flex-shrink-0'>{formatMoney(item.amounts?.[index], item.currency)}</span>
                <span className='flex-shrink-0'>{downloadButton}</span>
                <span className='flex-shrink-0 w-5'>{itemOptionButton(index)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {showOptionList && (
        <OptionList
          anchorRect={anchorRect}
          onClose={() => setShowOptionList(false)}
          status={selectedStatus}
          originalStatus={selectedOriginalStatus}
          hasReceipt={Boolean(item.receiptUrl)}
          onDownloadReceipt={handleDownloadReceipt}
          onViewInvoice={handleViewInvoice}
        />
      )}
    </div>
  );
};

export default PaymentsRow;
